import { z } from "zod";
import { EntityBase, Name, Note } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";

/** Performance evaluation criterion (scale 1-5, weight in per mille). */
export const PerformanceCriterionInput = z.object({
  name: Name,
  weightPerMille: z.number().int().min(1).max(1000),
  description: z.string().max(1000).optional().default(""),
  isLateness: z.boolean().optional().default(false),
});
export type PerformanceCriterionInput = z.infer<typeof PerformanceCriterionInput>;

export const PerformanceCriterion = EntityBase.extend({
  name: Name,
  weightPerMille: z.number().int(),
  scaleMin: z.number().int().default(1),
  scaleMax: z.number().int().default(5),
  description: z.string(),
  isLateness: z.boolean(),
});
export type PerformanceCriterion = z.infer<typeof PerformanceCriterion>;

/** Monthly score for one person and one criterion. */
export const PerformanceScoreInput = z.object({
  personId: Id,
  year: z.number().int().min(1390).max(1500),
  month: z.number().int().min(1).max(12),
  criterionId: Id,
  score: z.number().int().min(1).max(5),
  note: Note.optional().default(""),
});
export type PerformanceScoreInput = z.infer<typeof PerformanceScoreInput>;

export const PerformanceScore = EntityBase.extend({
  personId: Id,
  year: z.number().int(),
  month: z.number().int(),
  criterionId: Id,
  score: z.number().int(),
  note: z.string(),
  scoredBy: Id.nullable(),
});
export type PerformanceScore = z.infer<typeof PerformanceScore>;

/** Bonus base («مبنای پاداش») set manually by the manager. */
export const PerformanceBonusBaseInput = z.object({
  personId: Id,
  year: z.number().int().min(1390).max(1500),
  month: z.number().int().min(1).max(12),
  amountRial: Rial,
  notes: Note.optional().default(""),
});
export type PerformanceBonusBaseInput = z.infer<typeof PerformanceBonusBaseInput>;

export const PerformanceBonusBase = EntityBase.extend({
  personId: Id,
  year: z.number().int(),
  month: z.number().int(),
  amountRial: Rial,
  notes: z.string(),
  setBy: Id.nullable(),
});
export type PerformanceBonusBase = z.infer<typeof PerformanceBonusBase>;

/** Row representing a person's performance in a given Jalali month. */
export interface PersonMonthlyPerformance {
  personId: string;
  displayName: string;
  positionId: string | null;
  positionName: string | null;
  year: number;
  month: number;
  scores: Record<
    string,
    {
      scoreId?: string;
      criterionId: string;
      criterionName: string;
      score: number;
      weightPerMille: number;
      note: string;
    }
  >;
  weightedTotalPerMille: number;
  rank: number;
  bonusBaseRial: number | null;
  bonusNotes: string | null;
  lateMinutes: number;
  suggestedLatenessScore: number | null;
}
